import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, Check, X, RefreshCw, Loader2, ShieldCheck } from "lucide-react";
import { PasserellePaiementCard } from "@/components/PasserellePaiementCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGES = {
  en_verification: "bg-amber-100 text-amber-800 border-amber-200",
  accepte: "bg-[#0A5C36]/10 text-[#0A5C36] border-[#0A5C36]/20",
  refuse: "bg-red-100 text-red-700 border-red-200",
};

const jour = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : null;

/** File d'attente des paiements Mobile Money déclarés par les membres.
 *  Un seul clic sur « Valider » ouvre immédiatement l'accès du client. */
export const PaiementsDirectsSection = ({ adminId }) => {
  const [paiements, setPaiements] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [enCours, setEnCours] = useState(null);

  const charger = useCallback(() => {
    setChargement(true);
    axios
      .get(`${API}/admin/paiements-directs`, { params: { admin_id: adminId } })
      .then((r) => setPaiements(r.data))
      .catch(() => {})
      .finally(() => setChargement(false));
  }, [adminId]);

  useEffect(() => { charger(); }, [charger]);

  const trancher = async (transaction_id, accepte) => {
    if (enCours) return;
    setEnCours(transaction_id);
    try {
      const r = await axios.post(`${API}/admin/paiements-directs/${transaction_id}/valider`,
        { admin_id: adminId, accepte });
      const statut = accepte ? "accepte" : "refuse";
      const expire = r.data?.acces?.expire_le || null;
      setPaiements((liste) => liste.map((p) =>
        p.transaction_id === transaction_id ? { ...p, statut, expire_le: expire } : p));
      if (accepte) {
        toast.success("Accès ouvert !", {
          description: expire
            ? `${r.data.acces.offre} actif jusqu'au ${jour(expire)}. Le client reçoit son reçu par SMS.`
            : "L'abonnement du client est actif et le reçu SMS est parti.",
        });
      } else {
        toast.success("Paiement refusé.");
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Erreur");
    } finally {
      setEnCours(null);
    }
  };

  const enAttente = paiements.filter((p) => p.statut === "en_verification");
  const traites = paiements.filter((p) => p.statut !== "en_verification");
  const ordonnes = [...enAttente, ...traites];

  return (
    <section className="mt-14" data-testid="paiements-directs-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 flex items-center gap-2.5">
          <Wallet size={24} className="text-[#0A5C36]" /> Paiements Mobile Money à valider
          {enAttente.length > 0 && (
            <span className="text-xs font-bold bg-amber-500 text-white rounded-full px-2.5 py-1" data-testid="paiements-directs-compteur">
              {enAttente.length}
            </span>
          )}
        </h2>
        <button onClick={charger} data-testid="paiements-directs-refresh"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 hover:text-stone-900 border border-[#E5DFD5] bg-white rounded-full px-3 py-1.5 transition-colors">
          <RefreshCw size={13} className={chargement ? "animate-spin" : ""} /> Rafraîchir
        </button>
      </div>

      <PasserellePaiementCard adminId={adminId} />

      {enAttente.length > 0 && (
        <p className="mt-5 text-xs font-semibold text-stone-500" data-testid="paiements-directs-aide">
          Un clic sur « Valider » ouvre l'accès du client immédiatement et lui envoie son reçu par SMS.
        </p>
      )}

      <div className="mt-3 space-y-2" data-testid="paiements-directs-liste">
        {ordonnes.length === 0 ? (
          <p className="text-sm text-stone-400">Aucun paiement déclaré pour le moment.</p>
        ) : (
          ordonnes.map((p, i) => (
            <div key={`${p.transaction_id}-${p.reference_declaree || i}`}
              data-testid={`paiement-direct-${p.transaction_id}`}
              className={`bg-white border rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3 transition-colors ${
                p.statut === "en_verification" ? "border-amber-300" : "border-[#E5DFD5]"}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="font-semibold text-stone-900 text-sm">
                  {p.membre?.firstname} · {p.montant?.toLocaleString("fr-FR")} FCFA · {p.category?.toUpperCase()} ({p.period === "week" ? "semaine" : "mois"})
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  {p.operateur} · réf <b className="text-stone-700">{p.reference_declaree}</b> ·{" "}
                  {p.trace_pci?.telephone_masque || "—"} · {new Date(p.created_at).toLocaleString("fr-FR")}
                </div>
                {p.statut === "accepte" && p.expire_le && (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-bold text-[#0A5C36]"
                    data-testid={`paiement-acces-${p.transaction_id}`}>
                    <ShieldCheck size={13} /> Accès ouvert jusqu'au {jour(p.expire_le)}
                  </div>
                )}
              </div>
              <span className={`text-xs font-semibold border rounded-full px-3 py-1 ${BADGES[p.statut] || "bg-stone-100 text-stone-600 border-stone-200"}`}
                data-testid={`paiement-statut-${p.transaction_id}`}>
                {p.statut === "en_verification" ? "À vérifier" : p.statut === "accepte" ? "Validé" : "Refusé"}
              </span>
              {p.statut === "en_verification" && (
                <div className="flex gap-2">
                  <button onClick={() => trancher(p.transaction_id, true)}
                    disabled={enCours === p.transaction_id}
                    data-testid={`paiement-valider-${p.transaction_id}`}
                    className="inline-flex items-center gap-1.5 bg-[#0A5C36] hover:bg-[#084027] disabled:opacity-60 text-white text-xs font-semibold px-3.5 py-2 rounded-full transition-colors">
                    {enCours === p.transaction_id
                      ? <Loader2 size={13} className="animate-spin" />
                      : <Check size={13} />}
                    Valider & ouvrir l'accès
                  </button>
                  <button onClick={() => trancher(p.transaction_id, false)}
                    disabled={enCours === p.transaction_id}
                    data-testid={`paiement-refuser-${p.transaction_id}`}
                    className="inline-flex items-center gap-1.5 bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60 text-xs font-semibold px-3.5 py-2 rounded-full transition-colors">
                    <X size={13} /> Refuser
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </section>
  );
};
